import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Eye, FileText, Image as ImageIcon, Mic, Clock } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface SubmissionsListProps {
  linkId: string;
}

const SUBMISSION_TYPE_CONFIG: Record<string, { label: string; icon: any; color: string }> = {
  photo: { label: "Foto", icon: ImageIcon, color: "bg-blue-100 text-blue-700" },
  audio: { label: "Áudio", icon: Mic, color: "bg-purple-100 text-purple-700" },
  file: { label: "Arquivo", icon: FileText, color: "bg-green-100 text-green-700" },
  text: { label: "Texto", icon: FileText, color: "bg-gray-100 text-gray-700" },
};

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  pending: { label: "Pendente", color: "bg-yellow-100 text-yellow-700" },
  processed: { label: "Processado", color: "bg-green-100 text-green-700" },
  rejected: { label: "Descartado", color: "bg-red-100 text-red-700" },
};

export function SubmissionsList({ linkId }: SubmissionsListProps) {
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [previewSubmission, setPreviewSubmission] = useState<any | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    loadSubmissions();
  }, [linkId]);

  const loadSubmissions = async () => {
    try {
      const { data, error } = await supabase
        .from('assisted_submissions')
        .select('*')
        .eq('link_id', linkId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSubmissions(data || []);
    } catch (error) {
      console.error('Error loading submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async (submission: any) => {
    setPreviewSubmission(submission);

    if (submission.submission_type === 'text') {
      setPreviewUrl(null);
      return;
    }

    if (submission.file_path) {
      try {
        const { data: { signedUrl }, error } = await supabase.storage
          .from('assisted-submissions')
          .createSignedUrl(submission.file_path, 60 * 60);

        if (error) throw error;
        setPreviewUrl(signedUrl);
      } catch (error) {
        console.error('Error getting signed URL:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="text-center py-4 text-sm text-muted-foreground">
        Carregando submissões...
      </div>
    );
  }

  if (submissions.length === 0) {
    return (
      <div className="text-center py-4 text-sm text-muted-foreground">
        Nenhuma submissão ainda
      </div>
    );
  }

  return (
    <>
      <div className="mt-4 border-t pt-4 space-y-2">
        <h4 className="text-sm font-medium mb-3">Submissões Recebidas</h4>
        <div className="space-y-2">
          {submissions.map((submission) => {
            const typeConfig = SUBMISSION_TYPE_CONFIG[submission.submission_type] || SUBMISSION_TYPE_CONFIG.file;
            const statusConfig = STATUS_CONFIG[submission.status] || STATUS_CONFIG.pending;
            const TypeIcon = typeConfig.icon;
            const date = new Date(submission.created_at);

            return (
              <div
                key={submission.id}
                className="flex items-center justify-between p-3 bg-muted/50 rounded-lg text-sm"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <TypeIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium truncate">
                        {submission.file_name || 'Submissão'}
                      </span>
                      <Badge variant="outline" className={`text-xs ${typeConfig.color}`}>
                        {typeConfig.label}
                      </Badge>
                      <Badge variant="outline" className={`text-xs ${statusConfig.color}`}>
                        {statusConfig.label}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                      <Clock className="h-3 w-3" />
                      {formatDistanceToNow(date, { addSuffix: true, locale: ptBR })}
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePreview(submission)}
                  className="flex-shrink-0"
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={!!previewSubmission} onOpenChange={() => { setPreviewSubmission(null); setPreviewUrl(null); }}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>
              {previewSubmission?.file_name || 'Visualização'}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-auto max-h-[60vh]">
            {previewSubmission?.submission_type === 'text' && (
              <div className="p-4 bg-muted rounded-lg whitespace-pre-wrap">
                {previewSubmission.text_content}
              </div>
            )}
            {previewSubmission?.submission_type === 'photo' && previewUrl && (
              <img src={previewUrl} alt="Preview" className="w-full h-auto rounded-lg" />
            )}
            {previewSubmission?.submission_type === 'audio' && previewUrl && (
              <audio controls className="w-full" src={previewUrl} />
            )}
            {previewSubmission?.submission_type === 'file' && previewUrl && (
              <iframe
                src={previewUrl}
                className="w-full h-96 rounded-lg border"
                title="Preview do arquivo"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
